import React, { useState, useEffect } from "react";
import { ShoppingBag, Plus, Minus, X, Check } from "lucide-react";
import useReveal from "../hooks/useReveal";
import { PRODUCTS, IMAGES } from "../mock";
import { useToast } from "../hooks/use-toast";
import { fetchProducts } from "../api";

const CATS = ["All", "Serums", "Moisturisers", "Treatments", "Cleansers", "Eye Care", "Protection"];

export default function Shop() {
  const [cat, setCat] = useState("All");
  const [cart, setCart] = useState([]);
  const [open, setOpen] = useState(false);
  const [products, setProducts] = useState(PRODUCTS);
  const { toast } = useToast();
  useEffect(() => {
    fetchProducts().then((d) => { if (d && d.length) setProducts(d); }).catch((e) => console.error("Products load failed:", e));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  useReveal([cat, products]);

  const list = cat === "All" ? products : products.filter((p) => p.category === cat);

  const add = (p) => {
    setCart((c) => {
      const ex = c.find((i) => i.id === p.id);
      if (ex) return c.map((i) => (i.id === p.id ? { ...i, qty: i.qty + 1 } : i));
      return [...c, { ...p, qty: 1 }];
    });
    toast({ title: "Added to bag", description: p.name });
  };
  const changeQty = (id, d) =>
    setCart((c) => c.map((i) => (i.id === id ? { ...i, qty: Math.max(1, i.qty + d) } : i)));
  const remove = (id) => setCart((c) => c.filter((i) => i.id !== id));

  const count = cart.reduce((n, i) => n + i.qty, 0);
  const total = cart.reduce((n, i) => n + i.qty * i.price, 0);

  return (
    <main className="pt-20">
      <section className="relative py-32 bg-charcoal overflow-hidden">
        <div className="absolute inset-0 opacity-40">
          <img src={IMAGES.serum} alt="" className="w-full h-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-t from-charcoal via-charcoal/70 to-charcoal/40" />
        </div>
        <div className="relative z-10 max-w-[1300px] mx-auto px-6 text-center">
          <span className="eyebrow text-gold">The Boutique</span>
          <h1 className="font-display text-5xl lg:text-6xl font-light text-cream mt-5 leading-tight">
            High-end beauty, <span className="italic text-gold">bottled</span>
          </h1>
        </div>
      </section>

      <section className="bg-cream py-24">
        <div className="max-w-[1300px] mx-auto px-6">
          <div className="flex flex-wrap items-center justify-center gap-3 mb-16">
            {CATS.map((c) => (
              <button
                key={c}
                onClick={() => setCat(c)}
                className={`px-5 py-2.5 text-xs tracking-[0.16em] uppercase transition-all duration-300 border ${
                  cat === c ? "bg-charcoal text-cream border-charcoal" : "bg-transparent text-muted-ink border-gold/40 hover:border-gold hover:text-gold"
                }`}
              >
                {c}
              </button>
            ))}
          </div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-8">
            {list.map((p, i) => (
              <div key={p.id} className="group reveal" style={{ transitionDelay: `${(i % 3) * 80}ms` }}>
                <div className="img-zoom rounded-sm overflow-hidden bg-white aspect-[4/5] relative">
                  <img src={p.image} alt={p.name} className="w-full h-full object-cover" />
                  <button
                    onClick={() => add(p)}
                    className="absolute bottom-4 left-1/2 -translate-x-1/2 btn-gold opacity-0 group-hover:opacity-100 translate-y-3 group-hover:translate-y-0 transition-all duration-500 flex items-center gap-2"
                  >
                    <Plus size={14} /> Add to Bag
                  </button>
                </div>
                <div className="mt-5 text-center">
                  <span className="eyebrow text-gold">{p.category}</span>
                  <h3 className="font-display text-xl text-ink mt-1">{p.name}</h3>
                  <p className="font-sans text-muted-ink text-sm mt-2 leading-relaxed">{p.description}</p>
                  <p className="font-serif-alt text-gold text-lg mt-3">${p.price}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Floating cart button */}
      <button
        onClick={() => setOpen(true)}
        className="fixed bottom-8 right-8 z-40 w-16 h-16 rounded-full bg-charcoal text-gold border border-gold flex items-center justify-center shadow-xl hover:scale-105 transition-transform"
        aria-label="Open bag"
      >
        <ShoppingBag size={22} />
        {count > 0 && (
          <span className="absolute -top-1 -right-1 bg-gold text-charcoal text-xs w-6 h-6 rounded-full flex items-center justify-center font-medium">{count}</span>
        )}
      </button>

      {/* Cart drawer */}
      <div className={`fixed inset-0 z-50 transition-all duration-300 ${open ? "visible" : "invisible"}`}>
        <div className={`absolute inset-0 bg-charcoal/60 transition-opacity duration-300 ${open ? "opacity-100" : "opacity-0"}`} onClick={() => setOpen(false)} />
        <aside className={`absolute right-0 top-0 h-full w-full max-w-md bg-cream shadow-2xl transition-transform duration-500 flex flex-col ${open ? "translate-x-0" : "translate-x-full"}`}>
          <div className="flex items-center justify-between px-7 py-6 border-b border-gold/20">
            <h3 className="font-display text-2xl text-ink">Your Bag</h3>
            <button onClick={() => setOpen(false)} className="text-muted-ink hover:text-ink"><X size={22} /></button>
          </div>
          <div className="flex-1 overflow-y-auto px-7 py-4">
            {cart.length === 0 ? (
              <p className="text-muted-ink font-sans text-sm text-center mt-16">Your bag is empty.</p>
            ) : (
              cart.map((i) => (
                <div key={i.id} className="flex gap-4 py-5 border-b border-gold/15">
                  <img src={i.image} alt={i.name} className="w-20 h-24 object-cover rounded-sm" />
                  <div className="flex-1">
                    <h4 className="font-display text-lg text-ink leading-tight">{i.name}</h4>
                    <p className="text-gold text-sm mt-1">${i.price}</p>
                    <div className="flex items-center gap-3 mt-3">
                      <button onClick={() => changeQty(i.id, -1)} className="w-7 h-7 border border-gold/40 flex items-center justify-center text-ink hover:bg-gold hover:text-charcoal transition-colors"><Minus size={12} /></button>
                      <span className="text-sm w-5 text-center">{i.qty}</span>
                      <button onClick={() => changeQty(i.id, 1)} className="w-7 h-7 border border-gold/40 flex items-center justify-center text-ink hover:bg-gold hover:text-charcoal transition-colors"><Plus size={12} /></button>
                    </div>
                  </div>
                  <button onClick={() => remove(i.id)} className="text-muted-ink hover:text-ink self-start"><X size={16} /></button>
                </div>
              ))
            )}
          </div>
          <div className="px-7 py-6 border-t border-gold/20">
            <div className="flex items-center justify-between mb-5">
              <span className="eyebrow text-muted-ink">Subtotal</span>
              <span className="font-display text-2xl text-ink">${total}</span>
            </div>
            <button
              disabled={cart.length === 0}
              onClick={() => { toast({ title: "Thank you!", description: "This is a demo checkout." }); setCart([]); setOpen(false); }}
              className="btn-gold w-full flex items-center justify-center gap-2 disabled:opacity-40 disabled:cursor-not-allowed"
            >
              <Check size={15} /> Checkout
            </button>
          </div>
        </aside>
      </div>
    </main>
  );
}
